import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';
import Footer from '../home-page/components/Footer';

const TermsOfService = () => {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MainNavigation />
      <main className="flex-1 py-8 md:py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-sm text-muted-foreground hover:text-primary transition-smooth mb-4">

              <Icon name="ArrowLeft" size={16} />
              <span>Back to Home</span>
            </Link>
            
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              Terms of Service
            </h1>
            
            <p className="font-body text-base text-muted-foreground">
              <strong>Effective Date:</strong> February 11, 2026
            </p>
          </div>

          {/* Legal Notice - Prominent */}
          <div className="bg-destructive/10 border-2 border-destructive rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={24} className="text-destructive flex-shrink-0 mt-1" />
              <div>
                <p className="font-heading font-bold text-lg text-destructive mb-2">IMPORTANT:</p>
                <p className="font-body text-base text-foreground">
                  Draggin Trailers LLC is a technology platform only. We do not own, rent, lease, maintain, inspect, or
transport trailers listed on the Platform. All rental agreements are solely between trailer owners ("Owners")
and renters ("Renters"). Draggin Trailers is not a party to any rental agreement.
                </p>
              </div>
            </div>
          </div>

          {/* Introductory Terms Text */}
          <div className="mb-8">
            <p className="font-body text-base text-muted-foreground leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of the Draggin Trailers LLC website, mobile applications, and services (collectively, the "Platform"). Draggin Trailers LLC ("Draggin Trailers," "we," "us," or "our") operates a peer-to-peer marketplace that allows trailer owners to list trailers for rent and allows renters to book those trailers. By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree, do not use the Platform.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-4">
            {/* Marketplace Role */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('marketplace-role')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="Building2" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Marketplace Role</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['marketplace-role'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['marketplace-role'] &&
              <div className="px-6 pb-6">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    All rental agreements are entered into directly between trailer Owners and Renters. Draggin Trailers LLC is not a party to any rental transaction.
                  </p>
                </div>
              }
            </div>

            {/* No Insurance Provided */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('no-insurance')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="ShieldOff" size={24} className="text-destructive" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">No Insurance Provided</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['no-insurance'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['no-insurance'] &&
              <div className="px-6 pb-6">
                  <div className="bg-destructive/5 border border-destructive/20 rounded-md p-4">
                    <p className="font-body text-base text-foreground leading-relaxed">
                      Draggin Trailers LLC does NOT provide insurance, liability coverage, collision coverage, cargo coverage, or protection plans of any kind.
                    </p>
                  </div>
                </div>
              }
            </div>

            {/* Assumption of Risk */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('assumption-risk')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={24} className="text-warning" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Assumption of Risk</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['assumption-risk'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['assumption-risk'] &&
              <div className="px-6 pb-6">
                  <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                    Trailer use and towing involve inherent risks including accidents, mechanical failure, road hazards, and third-party negligence. By using the platform, you voluntarily assume all risks.
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Accidents and collisions during transport</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Mechanical failures and equipment malfunctions</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Road hazards and adverse weather conditions</span>
                    </li>
                    <li className="font-body text-base text-muted-foreground flex items-start space-x-2">
                      <Icon name="Circle" size={8} className="mt-2 flex-shrink-0" />
                      <span>Third-party negligence and unforeseen circumstances</span>
                    </li>
                  </ul>
                </div>
              }
            </div>

            {/* Limitation of Liability */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('limitation-liability')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="Scale" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Limitation of Liability</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['limitation-liability'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['limitation-liability'] &&
              <div className="px-6 pb-6">
                  <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                    To the maximum extent permitted by law, Draggin Trailers LLC shall not be liable for any injury, death, property damage, or loss arising from rentals facilitated through the platform.
                  </p>
                  <div className="bg-muted/50 rounded-md p-4">
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">
                      This includes, but is not limited to, direct, indirect, incidental, consequential, special, or punitive damages, even if Draggin Trailers LLC has been advised of the possibility of such damages.
                    </p>
                  </div>
                </div>
              }
            </div>

            {/* Arbitration & Class Action Waiver */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('arbitration')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="Gavel" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Arbitration & Class Action Waiver</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['arbitration'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['arbitration'] &&
              <div className="px-6 pb-6">
                  <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                    All disputes shall be resolved through binding arbitration administered by the American Arbitration Association. Users waive the right to participate in class action lawsuits.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircle2" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <p className="font-body text-sm text-muted-foreground">
                        <strong>Binding Arbitration:</strong> All claims must be resolved through individual arbitration
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircle2" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <p className="font-body text-sm text-muted-foreground">
                        <strong>Class Action Waiver:</strong> You waive the right to participate in class action lawsuits
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircle2" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <p className="font-body text-sm text-muted-foreground">
                        <strong>AAA Rules:</strong> Disputes will be administered under AAA Commercial Arbitration Rules
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Governing Law */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('governing-law')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Governing Law</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['governing-law'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['governing-law'] &&
              <div className="px-6 pb-6">
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    These Terms are governed by the laws of the State of Wisconsin, without regard to its conflict of law provisions. Any legal action or proceeding arising under these Terms will be brought exclusively in the federal or state courts located in Wisconsin.
                  </p>
                </div>
              }
            </div>

            {/* User Responsibilities */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('user-responsibilities')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">User Responsibilities</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['user-responsibilities'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['user-responsibilities'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Eligibility</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        You must be at least 18 years old and legally able to enter into contracts to use the Platform. By using the Platform, you represent that you meet these requirements.
                      </p>
                    </div>
                    <div>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Inspect trailers thoroughly before accepting rental</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Maintain appropriate insurance coverage for towing and cargo</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Operate trailers safely and in compliance with all applicable laws</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Return trailers in the same condition as received</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Provide accurate descriptions and condition reports</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Maintain trailers in safe, roadworthy condition</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Maintain appropriate insurance and registration</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Comply with all applicable safety regulations</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Payments & Fees</h3>
                      <p className="font-body text-sm text-muted-foreground">
                        Draggin Trailers may collect payments on behalf of Owners and charge service fees to Owners and/or Renters. All fees are disclosed at checkout. Failure to pay may result in account suspension or legal action.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Revenue, Fees & Tax Collection */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('revenue-fees')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="DollarSign" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Revenue, Fees & Tax Collection</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['revenue-fees'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['revenue-fees'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    {/* Marketplace Role in Payments */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">1. Marketplace Role in Payments</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Draggin Trailers LLC operates solely as a technology marketplace facilitating payments between Trailer Owners and Renters. The Company does not set rental prices and is not a party to rental agreements.
                      </p>
                      <p className="font-body text-sm text-muted-foreground">
                        All payments are processed through third-party payment processors.
                      </p>
                    </div>

                    {/* Rental Pricing */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">2. Rental Pricing</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Trailer Owners independently determine:
                      </p>
                      <ul className="space-y-2 ml-6 mb-2">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Rental price</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Availability</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Security deposit (if applicable)</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-muted-foreground">
                        Draggin Trailers LLC does not control pricing but may display estimated totals including applicable taxes and fees.
                      </p>
                    </div>

                    {/* Platform Fees */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">3. Platform Fees</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Draggin Trailers LLC charges a platform service fee for facilitating transactions.
                      </p>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Unless otherwise stated:
                      </p>
                      <ul className="space-y-2 ml-6 mb-2">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The platform fee is calculated as a percentage of the rental price.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The fee is automatically deducted prior to payout to the Owner.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The fee percentage may be updated from time to time with notice.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Platform fees are non-refundable except where required by law.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Sales Tax & Marketplace Facilitator Compliance */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">4. Sales Tax & Marketplace Facilitator Compliance</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Where required by federal, state, or local law, Draggin Trailers LLC may:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Calculate applicable sales or rental taxes</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Collect such taxes from the Renter at checkout</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Remit collected taxes to the appropriate taxing authority</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        In jurisdictions where Draggin Trailers qualifies as a "Marketplace Facilitator" or similar designation, the Company will collect and remit applicable taxes as required by law.
                      </p>
                      <div className="bg-warning/10 border border-warning/30 rounded-md p-3">
                        <p className="font-body text-sm text-foreground mb-2">
                          <strong>Owners remain responsible for:</strong>
                        </p>
                        <ul className="space-y-1 ml-6">
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Any income taxes owed on rental earnings</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Any taxes not required to be collected by the Platform</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Compliance with local business licensing requirements</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Payment Flow & Payouts */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">5. Payment Flow & Payouts</h3>
                      <div className="bg-muted/50 rounded-md p-4 mb-3">
                        <p className="font-body text-sm text-foreground mb-2">
                          <strong>At checkout:</strong>
                        </p>
                        <p className="font-body text-sm text-muted-foreground mb-2">The Renter pays:</p>
                        <ul className="space-y-1 ml-6 mb-3">
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Rental Price</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Applicable Sales Tax</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Any disclosed fees</span>
                          </li>
                        </ul>
                        <p className="font-body text-sm text-foreground mb-2">
                          <strong>Draggin Trailers LLC:</strong>
                        </p>
                        <ul className="space-y-1 ml-6">
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Retains the platform service fee</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Remits collected sales tax where required</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Releases the remaining balance to the Owner</span>
                          </li>
                        </ul>
                      </div>
                      <p className="font-body text-sm text-muted-foreground">
                        Payout timing may depend on payment processor rules, dispute status, fraud review, or refund activity.
                      </p>
                    </div>

                    {/* Refunds & Adjustments */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">6. Refunds & Adjustments</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        In the event of:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Cancellations</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Chargebacks</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Disputes</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Required tax adjustments</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Draggin Trailers LLC reserves the right to:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Offset future payouts</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Recover previously paid amounts</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Adjust tax remittances as required</span>
                        </li>
                      </ul>
                    </div>

                    {/* No Tax or Financial Advice */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-destructive" />
                        <span>7. No Tax or Financial Advice</span>
                      </h3>
                      <p className="font-body text-sm text-foreground">
                        Nothing contained on the Platform constitutes tax, accounting, or legal advice. Users are encouraged to consult qualified professionals regarding their specific obligations.
                      </p>
                    </div>

                    {/* Tax Reporting & IRS Form 1099-K Disclosure */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">8. Tax Reporting & IRS Form 1099-K Disclosure</h3>
                      
                      {/* Tax Reporting Obligations */}
                      <div className="mb-4">
                        <h4 className="font-heading font-semibold text-base text-foreground mb-2">Tax Reporting Obligations</h4>
                        <p className="font-body text-sm text-muted-foreground mb-2">
                          The Platform complies with applicable U.S. federal and state tax reporting requirements. Pursuant to Internal Revenue Code §6050W, the Platform may be required to file IRS Form 1099-K (Payment Card and Third Party Network Transactions) for Owners who meet applicable reporting thresholds.
                        </p>
                      </div>

                      {/* Reporting Thresholds */}
                      <div className="mb-4">
                        <h4 className="font-heading font-semibold text-base text-foreground mb-2">Reporting Thresholds</h4>
                        <p className="font-body text-sm text-muted-foreground mb-2">
                          The Platform will issue Form 1099-K to Owners when required by law. Federal reporting thresholds are subject to change and may include:
                        </p>
                        <ul className="space-y-2 ml-6 mb-2">
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Gross payments exceeding the IRS threshold for the applicable tax year; or</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Any other threshold required by federal or state law.</span>
                          </li>
                        </ul>
                        <p className="font-body text-sm text-muted-foreground">
                          Certain states may impose lower reporting thresholds than federal requirements.
                        </p>
                      </div>

                      {/* Gross Payment Reporting */}
                      <div className="mb-4">
                        <h4 className="font-heading font-semibold text-base text-foreground mb-2">Gross Payment Reporting</h4>
                        <p className="font-body text-sm text-muted-foreground">
                          Form 1099-K reports gross transaction amounts, meaning total payments processed through the Platform before deductions for fees, refunds, chargebacks, or other adjustments. Owners are responsible for determining their taxable income after allowable deductions.
                        </p>
                      </div>

                      {/* Taxpayer Information Requirement */}
                      <div className="mb-4">
                        <h4 className="font-heading font-semibold text-base text-foreground mb-2">Taxpayer Information Requirement</h4>
                        <p className="font-body text-sm text-muted-foreground mb-2">
                          To receive payments and remain eligible for continued use of the Platform, Owners must provide accurate tax identification information, including:
                        </p>
                        <ul className="space-y-2 ml-6 mb-2">
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Legal name</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Business name (if applicable)</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Address</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Taxpayer Identification Number (TIN), Social Security Number (SSN), or Employer Identification Number (EIN)</span>
                          </li>
                        </ul>
                        <p className="font-body text-sm text-muted-foreground">
                          Failure to provide accurate tax information may result in payment withholding, reporting delays, backup withholding as required by the IRS, suspension of payouts, or account restrictions.
                        </p>
                      </div>

                      {/* Backup Withholding */}
                      <div className="mb-4">
                        <h4 className="font-heading font-semibold text-base text-foreground mb-2">Backup Withholding</h4>
                        <p className="font-body text-sm text-muted-foreground">
                          If the IRS notifies the Platform that an Owner's tax information is incorrect or missing, the Platform may be required to apply backup withholding at the applicable IRS rate and remit withheld amounts to the IRS.
                        </p>
                      </div>

                      {/* Owner Responsibility */}
                      <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                        <h4 className="font-heading font-semibold text-base text-foreground mb-2">Owner Responsibility</h4>
                        <p className="font-body text-sm text-foreground mb-2">
                          Owners are solely responsible for:
                        </p>
                        <ul className="space-y-2 ml-6">
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Determining and paying all federal, state, and local taxes arising from transactions processed through the Platform;</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Filing required tax returns;</span>
                          </li>
                          <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                            <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                            <span>Maintaining their own financial records.</span>
                          </li>
                        </ul>
                        <p className="font-body text-sm text-foreground mt-3">
                          The Platform does not provide tax advice. Owners should consult a qualified tax professional regarding their specific tax obligations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Security Deposits */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('security-deposits')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Security Deposits</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['security-deposits'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['security-deposits'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    <p className="font-body text-sm text-muted-foreground">
                      Trailer Owners may require a refundable security deposit.
                    </p>

                    {/* Collection */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Collection</h3>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Deposits are collected at checkout.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Deposits are held by Draggin Trailers LLC or its payment processor.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Deposits are not considered revenue of the Platform.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Holding Period */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Holding Period</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Deposits are held until:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The rental period ends; and</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The Owner confirms no damage; or</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The dispute window expires.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Damage Claims */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Damage Claims</h3>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        If damage is alleged:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Owner must submit evidence within 2 hours.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Renter is notified.</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-muted-foreground mb-2">
                        Platform may:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Release full deposit</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Release partial deposit</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Hold funds pending dispute resolution
                        </span>
                        </li>
                      </ul>
                    </div>

                    {/* Platform Discretion */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Platform Discretion</h3>
                      <p className="font-body text-sm text-muted-foreground">
                        Draggin Trailers LLC may make final determinations in deposit disputes but is not obligated to investigate beyond reasonable review.
                      </p>
                    </div>

                    {/* Automatic Release & Dispute Window */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Automatic Release & Dispute Window</h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        Unless a damage claim or dispute is initiated, security deposits will be automatically released and refunded to the Renter's original payment method within seventy-two (72) hours after the scheduled rental period has ended.
                      </p>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        If a Trailer Owner reports alleged damage, missing equipment, excessive cleaning requirements, late return, or other rental violations within the seventy-two (72) hour review period, the deposit may be temporarily withheld pending review and resolution of the claim.
                      </p>
                      <p className="font-body text-sm text-muted-foreground">
                        If no damage or violation is reported within the seventy-two (72) hour period, the deposit will be deemed approved for release and Draggin Trailers LLC will have no further obligation to investigate claims submitted after that time.
                      </p>
                    </div>

                    {/* No Insurance Substitute */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-warning" />
                        <span>No Insurance Substitute</span>
                      </h3>
                      <p className="font-body text-sm text-foreground">
                        Security deposits are not insurance and may not fully cover damages.
                      </p>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Damage Reporting Procedure */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('damage-reporting')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="FileWarning" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Damage Reporting Procedure</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['damage-reporting'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['damage-reporting'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    <p className="font-body text-sm text-muted-foreground">To initiate a dispute claim, the Trailer Owner must submit all required documentation within seventy-two (72) hours of rental completion.

                  </p>

                    {/* Required Evidence */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Required Evidence</h3>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Time-stamped photos or videos clearly showing alleged damage.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Pre-rental condition documentation (if available).</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Written description of the damage.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Estimated repair cost or invoice, if available.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Confirmation that the damage occurred during the rental period.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Claim Denial Warning */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-destructive" />
                        <span>Documentation Requirements</span>
                      </h3>
                      <p className="font-body text-sm text-foreground">
                        Failure to provide sufficient documentation within the required timeframe may result in automatic denial of the claim.
                      </p>
                    </div>

                    {/* Claim Qualification */}
                    <div className="bg-muted/50 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">Claim Qualification</h3>
                      <p className="font-body text-sm text-muted-foreground">
                        Normal wear and tear, minor cosmetic issues, or pre-existing conditions may not qualify for deposit deductions.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Platform Review & Determination */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('platform-review')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="Search" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Platform Review & Determination</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['platform-review'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['platform-review'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    <p className="font-body text-sm text-muted-foreground mb-3">
                      Draggin Trailers LLC may, but is not obligated to:
                    </p>

                    {/* Platform Actions */}
                    <div>
                      <ul className="space-y-2 ml-6 mb-4">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Review submitted evidence.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Request additional documentation.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Facilitate communication between parties.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Platform Discretion */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Platform Discretion</h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        Draggin Trailers LLC reserves the sole and absolute discretion to determine:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Whether a deposit claim is valid.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The amount, if any, to be released to the Owner.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Whether the full deposit should be refunded to the Renter.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Final Determinations */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2 flex items-center space-x-2">
                        <Icon name="Gavel" size={20} className="text-warning" />
                        <span>Final Determinations</span>
                      </h3>
                      <p className="font-body text-sm text-foreground">
                        All determinations made by Draggin Trailers LLC are final and binding to the extent permitted by law.
                      </p>
                    </div>

                    {/* Platform Role Clarification */}
                    <div className="bg-muted/50 rounded-md p-4">
                      <p className="font-body text-sm text-muted-foreground">
                        The Platform is not a mediator, insurer, or guarantor and is not responsible for independently verifying damage claims.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Partial Release of Funds */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('partial-release')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="DollarSign" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Partial Release of Funds</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['partial-release'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['partial-release'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    <p className="font-body text-sm text-muted-foreground mb-3">
                      The Platform may:
                    </p>

                    {/* Platform Actions */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Release a portion of the deposit to the Owner for verified damages; and</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Refund any remaining balance to the Renter.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Excess Damages */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-warning" />
                        <span>Damages Exceeding Deposit</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-2">
                        If damages exceed the deposit amount, Owners and Renters are responsible for resolving the remaining balance directly.
                      </p>
                      <p className="font-body text-sm text-muted-foreground">
                        Draggin Trailers LLC is not responsible for collection of additional damages.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Fraud, Chargebacks & Recovery Rights */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('fraud-chargebacks')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="ShieldAlert" size={24} className="text-destructive" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Fraud, Chargebacks & Recovery Rights</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['fraud-chargebacks'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['fraud-chargebacks'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    <p className="font-body text-sm text-muted-foreground mb-3">
                      If a Renter initiates a chargeback or payment dispute related to a deposit or damage claim:
                    </p>

                    {/* Platform Rights */}
                    <div>
                      <ul className="space-y-3 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Draggin Trailers LLC reserves the right to recover disputed amounts from future payouts owed to the Owner.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The Platform may temporarily suspend accounts during investigation.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The Platform may require additional documentation from either party.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Users agree to cooperate in good faith during any dispute investigation.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Warning Notice */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-destructive" />
                        <span>Account Suspension</span>
                      </h3>
                      <p className="font-body text-sm text-foreground">
                        Accounts may be temporarily suspended during chargeback investigations to protect all parties involved.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Damage, Liability & Indemnification */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('damage-liability')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="AlertOctagon" size={24} className="text-destructive" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Damage, Liability & Indemnification</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['damage-liability'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['damage-liability'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    {/* 1. Assumption of Risk */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">1. Assumption of Risk</h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        Renters acknowledge that towing and operating trailers involves inherent risks, including but not limited to:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Vehicle damage</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Property damage</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Mechanical failure</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Road hazards</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Personal injury</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Third-party liability</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground font-semibold">
                        Renters assume all risks associated with towing, loading, transporting, and operating rented trailers.
                      </p>
                    </div>

                    {/* 2. Renter Responsibility for Damage */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-warning" />
                        <span>2. Renter Responsibility for Damage</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        The Renter is financially responsible for:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>All physical damage occurring during the rental period</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Theft or loss</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Traffic violations</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Impound fees</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Toll charges</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Excess cleaning</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Late return penalties</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground font-semibold">
                        Responsibility begins at pickup and ends upon confirmed return.
                      </p>
                    </div>

                    {/* 3. Owner Responsibility */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">3. Owner Responsibility</h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        Trailer Owners represent and warrant that:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The trailer is legally registered where required</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The trailer is roadworthy at the start of the rental</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Tires, lights, brakes, and safety chains are functional</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The trailer complies with applicable state and federal transportation laws</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground font-semibold">
                        Owners are solely responsible for maintenance and mechanical safety.
                      </p>
                    </div>

                    {/* 4. No Platform Liability */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="ShieldOff" size={20} className="text-destructive" />
                        <span>4. No Platform Liability</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Draggin Trailers LLC:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Does not own trailers listed on the Platform</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Does not inspect trailers</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Does not guarantee mechanical condition</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Is not responsible for accidents, injuries, or damages</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Is not a party to rental agreements</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground font-semibold">
                        To the maximum extent permitted by law, the Platform disclaims liability arising from rentals facilitated through the Platform.
                      </p>
                    </div>

                    {/* 5. Indemnification */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">5. Indemnification</h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        Users agree to indemnify, defend, and hold harmless Draggin Trailers LLC and its officers, members, employees, and agents from any claims, damages, liabilities, losses, or expenses (including attorneys' fees) arising from:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Rental activity</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Violation of these Terms</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Violation of law</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Damage to persons or property</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Misrepresentation of trailer condition</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Improper towing or loading</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground font-semibold">
                        This indemnification obligation survives termination of the account.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Prohibited Use & Illegal Cargo */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('prohibited-use')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="ShieldX" size={24} className="text-destructive" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Prohibited Use & Illegal Cargo</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['prohibited-use'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['prohibited-use'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    {/* Illegal Activity Prohibited */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Ban" size={20} className="text-destructive" />
                        <span>Illegal Activity Prohibited</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Users may not use the Platform or any rented trailer to:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Transport illegal substances</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Transport stolen property</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Engage in human trafficking</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Commit fraud</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Transport hazardous materials without proper permits</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Violate federal, state, or local law</span>
                        </li>
                      </ul>
                    </div>

                    {/* Hazardous Materials Restriction */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Flame" size={20} className="text-warning" />
                        <span>Hazardous Materials Restriction</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Without proper legal authorization and compliance with applicable regulations, trailers may not be used to transport:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Explosives</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Flammable chemicals</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Toxic substances</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Regulated hazardous waste</span>
                        </li>
                      </ul>
                    </div>

                    {/* Law Enforcement Cooperation */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Law Enforcement Cooperation</h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        Draggin Trailers LLC reserves the right to:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Cooperate with law enforcement</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Provide transaction records when legally required</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Suspend accounts associated with suspected illegal activity</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Account Suspension & Fraud Enforcement */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('account-suspension')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="UserX" size={24} className="text-destructive" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Account Suspension & Fraud Enforcement</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['account-suspension'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['account-suspension'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    {/* Grounds for Suspension or Termination */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Ban" size={20} className="text-destructive" />
                        <span>1. Grounds for Suspension or Termination</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Draggin Trailers LLC may suspend or permanently terminate accounts for:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Fraudulent activity</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Chargebacks or payment disputes</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>False damage claims</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Misrepresentation of trailer condition</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Repeated cancellations</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Harassment or abuse</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Violation of these Terms</span>
                        </li>
                      </ul>
                    </div>

                    {/* Immediate Suspension */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-warning" />
                        <span>2. Immediate Suspension</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        The Platform may suspend accounts without prior notice if it determines that:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Fraud risk exists</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Safety concerns arise</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Legal violations are suspected</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Payment disputes are initiated</span>
                        </li>
                      </ul>
                    </div>

                    {/* Withholding of Funds */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="DollarSign" size={20} className="text-primary" />
                        <span>3. Withholding of Funds</span>
                      </h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        The Platform may:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Temporarily hold payouts</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Offset disputed amounts against future payouts</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Retain funds pending investigation</span>
                        </li>
                      </ul>
                    </div>

                    {/* No Liability for Suspension */}
                    <div className="bg-muted/50 border border-border rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="ShieldOff" size={20} className="text-muted-foreground" />
                        <span>4. No Liability for Suspension</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Draggin Trailers LLC shall not be liable for:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Lost profits</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Business interruption</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Reputation damage</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Indirect or consequential losses resulting from suspension</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Cleaning & Excessive Condition Fee */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('cleaning-excessive-condition')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="Sparkles" size={24} className="text-primary" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Cleaning & Excessive Condition Fee</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['cleaning-excessive-condition'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['cleaning-excessive-condition'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    {/* Return Condition Requirement */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="CheckCircle2" size={20} className="text-primary" />
                        <span>Return Condition Requirement</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Renters agree to return the trailer in substantially the same condition as it was at the time of pickup, excluding ordinary wear and tear from normal use.
                      </p>
                      <p className="font-body text-sm text-foreground mb-3">
                        The trailer must be returned:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Free of excessive dirt, debris, mud, waste, or residue</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Free of trash, personal items, or construction materials</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Free of unauthorized modifications or attachments</span>
                        </li>
                      </ul>
                    </div>

                    {/* Excessive Cleaning Fee */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="DollarSign" size={20} className="text-warning" />
                        <span>Excessive Cleaning Fee</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        If the trailer is returned in a condition requiring excessive cleaning beyond standard turnaround preparation, a flat cleaning fee of fifty dollars ($50.00) will be assessed.
                      </p>
                      <p className="font-body text-sm text-foreground mb-3">
                        This fee may be applied if, in the reasonable determination of the Trailer Owner and/or Draggin Trailers LLC, the trailer requires additional labor or services to restore it to rentable condition.
                      </p>
                      <p className="font-body text-sm text-foreground mb-3">
                        Examples of excessive cleaning include, but are not limited to:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Heavy mud accumulation</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Concrete, paint, or chemical residue</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Animal waste</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Spilled materials requiring pressure washing</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Construction debris or bulk trash</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Strong odors requiring deodorization</span>
                        </li>
                      </ul>
                    </div>

                    {/* Authorization to Charge */}
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Authorization to Charge</h3>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        By completing a booking, the Renter authorizes Draggin Trailers LLC to charge the $50 excessive cleaning fee to:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The payment method on file; or</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The security deposit, if applicable</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-muted-foreground">
                        The fee may be deducted prior to deposit release.
                      </p>
                    </div>

                    {/* Evidence Requirement */}
                    <div className="bg-muted/50 border border-border rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Camera" size={20} className="text-muted-foreground" />
                        <span>Evidence Requirement</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        To assess the cleaning fee, the Owner must submit reasonable supporting evidence within seventy-two (72) hours of rental completion, which may include:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Time-stamped photos or video</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Description of cleaning required</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground">
                        Failure to submit evidence within the required timeframe may result in denial of the cleaning fee request.
                      </p>
                    </div>

                    {/* No Limitation of Other Damages */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-destructive" />
                        <span>No Limitation of Other Damages</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        The excessive cleaning fee is separate from and in addition to:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Damage repair costs</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Late return fees</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Other contractual charges</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground">
                        If cleaning costs exceed $50 due to extraordinary conditions, such costs may be treated as damage under the Security Deposit and Damage provisions of these Terms.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Theft, Failure to Return & Unauthorized Retention */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('theft-failure-return')}
                className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">

                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={24} className="text-destructive" />
                  <h2 className="font-heading font-bold text-2xl text-foreground">Theft, Failure to Return & Unauthorized Retention</h2>
                </div>
                <Icon
                  name="ChevronDown"
                  size={24}
                  className={`text-muted-foreground transition-transform duration-200 ${openSections?.['theft-failure-return'] ? 'rotate-180' : ''}`} />

              </button>
              {openSections?.['theft-failure-return'] &&
              <div className="px-6 pb-6">
                  <div className="space-y-6">
                    {/* Obligation to Return Trailer */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="RotateCcw" size={20} className="text-primary" />
                        <span>Obligation to Return Trailer</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Renters agree to return the rented trailer:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>On or before the scheduled return date and time</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>To the agreed-upon location</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>In substantially the same condition as received (ordinary wear and tear excepted)</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground">
                        Failure to return a trailer as agreed constitutes a material breach of these Terms.
                      </p>
                    </div>

                    {/* Late Return */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Clock" size={20} className="text-warning" />
                        <span>Late Return</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        If a trailer is not returned at the scheduled time:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The rental period may automatically extend at the daily rental rate or a higher late-return rate as disclosed on the Platform.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Additional fees may be charged to the Renter's payment method.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The security deposit may be held pending resolution.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Draggin Trailers LLC may attempt to contact the Renter regarding the overdue trailer but has no obligation to do so.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Unauthorized Retention */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Ban" size={20} className="text-destructive" />
                        <span>Unauthorized Retention</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        If a trailer is not returned within twenty-four (24) hours after the scheduled return time and no communication has been made with the Owner, the rental may be deemed an unauthorized retention.
                      </p>
                      <p className="font-body text-sm text-foreground mb-3">
                        In such cases, the Owner may:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Report the trailer as stolen to law enforcement</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Provide rental records to authorities</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Pursue civil recovery remedies</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground mb-3">
                        Draggin Trailers LLC reserves the right to:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Suspend or permanently terminate the Renter's account</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Cooperate with law enforcement</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Provide transaction records as legally required</span>
                        </li>
                      </ul>
                    </div>

                    {/* Stolen Trailer Reporting Procedure */}
                    <div className="bg-muted/50 border border-border rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="FileText" size={20} className="text-foreground" />
                        <span>Stolen Trailer Reporting Procedure</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        If an Owner believes a trailer has been stolen or unlawfully retained, the Owner must:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Attempt to contact the Renter through the Platform.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>File a police report in the appropriate jurisdiction.</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Provide Draggin Trailers LLC with:</span>
                        </li>
                      </ul>
                      <ul className="space-y-2 ml-12 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="ChevronRight" size={12} className="mt-1.5 flex-shrink-0" />
                          <span>Copy of the police report</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="ChevronRight" size={12} className="mt-1.5 flex-shrink-0" />
                          <span>Rental agreement details</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="ChevronRight" size={12} className="mt-1.5 flex-shrink-0" />
                          <span>Trailer VIN or identification information</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="ChevronRight" size={12} className="mt-1.5 flex-shrink-0" />
                          <span>Any GPS tracking data (if available)</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-muted-foreground">
                        The Platform may, but is not obligated to, assist in providing rental documentation to law enforcement.
                      </p>
                    </div>

                    {/* Financial Responsibility */}
                    <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="DollarSign" size={20} className="text-primary" />
                        <span>Financial Responsibility</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        The Renter remains fully financially responsible for:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>The full replacement value of the trailer</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Towing, recovery, and repossession costs</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Lost rental income</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Legal fees and collection costs</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Any insurance deductible incurred by the Owner</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-muted-foreground mb-3">
                        If the trailer is not recovered, the Renter may be charged up to the fair market value of the trailer, less any amounts recovered through insurance.
                      </p>
                      <p className="font-body text-sm text-muted-foreground">
                        Draggin Trailers LLC may pursue collection of unpaid amounts, including through third-party collection agencies.
                      </p>
                    </div>

                    {/* Platform Not Responsible for Theft */}
                    <div className="bg-destructive/10 border border-destructive/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="ShieldOff" size={20} className="text-destructive" />
                        <span>Platform Not Responsible for Theft</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Draggin Trailers LLC:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Does not guarantee return of any trailer</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Does not provide theft protection</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Is not responsible for recovery of stolen property</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Is not liable for loss resulting from theft or unauthorized retention</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground">
                        The Platform acts solely as a technology facilitator and is not a bailee, custodian, or insurer of listed trailers.
                      </p>
                    </div>

                    {/* Civil & Criminal Remedies */}
                    <div className="bg-warning/10 border border-warning/30 rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Gavel" size={20} className="text-warning" />
                        <span>Civil & Criminal Remedies</span>
                      </h3>
                      <p className="font-body text-sm text-foreground mb-3">
                        Users acknowledge that theft or intentional failure to return rented property may constitute:
                      </p>
                      <ul className="space-y-2 ml-6 mb-3">
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Civil conversion</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Criminal theft</span>
                        </li>
                        <li className="font-body text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="Circle" size={8} className="mt-1.5 flex-shrink-0" />
                          <span>Fraud</span>
                        </li>
                      </ul>
                      <p className="font-body text-sm text-foreground">
                        The Platform reserves all rights to cooperate fully with law enforcement authorities and to pursue civil remedies where appropriate.
                      </p>
                    </div>

                    {/* Survival */}
                    <div className="bg-muted/50 border border-border rounded-md p-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-3 flex items-center space-x-2">
                        <Icon name="Shield" size={20} className="text-foreground" />
                        <span>Survival</span>
                      </h3>
                      <p className="font-body text-sm text-foreground">
                        The provisions of this section survive termination of the rental and termination of the user's account.
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Contact Information */}
            <section className="bg-primary/5 border border-primary/20 rounded-lg p-6">
              <h2 className="font-heading font-bold text-xl text-foreground mb-3 flex items-center space-x-2">
                <Icon name="Mail" size={20} className="text-primary" />
                <span>Questions About These Terms?</span>
              </h2>
              <p className="font-body text-sm text-muted-foreground mb-4">
                If you have any questions or concerns about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2">
                <p className="font-body text-sm text-foreground">
                  <strong>Email:</strong> Draggintrailers@gmail.com
                </p>
              </div>
            </section>

            {/* Acceptance Notice */}
            <div className="bg-muted/30 border border-border rounded-lg p-6 text-center">
              <Icon name="FileText" size={32} className="text-primary mx-auto mb-3" />
              <p className="font-body text-sm text-muted-foreground">
                By using Draggin Trailers, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>);

};

export default TermsOfService;